// src/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import { auth, db } from "./firebase";
import { ADMIN_UID } from "./config";
import {
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        const snap = await getDoc(doc(db, "users", firebaseUser.uid));
        if (snap.exists()) {
          const data = snap.data();
          // SECURITY: Only the hardcoded owner UID gets admin access.
          // Even if someone sets isAdmin: true in Firestore, it won't work
          // unless their UID matches the owner UID.
          data.isAdmin = firebaseUser.uid === ADMIN_UID;
          setUserData(data);
        } else {
          setUserData({ isAdmin: firebaseUser.uid === ADMIN_UID });
        }
      } else {
        setUser(null);
        setUserData(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const register = async (email, password, displayName) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(cred.user, { displayName });
    const profile = {
      uid: cred.user.uid,
      email,
      displayName,
      isAdmin: false,
      createdAt: Date.now(),
    };
    await setDoc(doc(db, "users", cred.user.uid), profile);
    // Override with hardcoded check
    profile.isAdmin = cred.user.uid === ADMIN_UID;
    setUserData(profile);
    return cred.user;
  };

  const login = async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const snap = await getDoc(doc(db, "users", cred.user.uid));
    const data = snap.exists() ? snap.data() : {};
    data.isAdmin = cred.user.uid === ADMIN_UID;
    setUserData(data);
    return cred.user;
  };

  const logout = () => signOut(auth);

  return (
    <AuthContext.Provider value={{ user, userData, loading, register, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
